import axiosInstance from '../utils/axios';

export interface Project {
  id: number;
  name: string;
  description: string;
  company_name: string;
  startDate: string;
  endDate: string;
  status: 'planning' | 'ongoing' | 'completed' | 'cancelled';
  manager: string;
  budget: number;
  qr_code?: string;
  require_login: boolean;
  agreement_required: boolean;
  agreement_title?: string;
  agreement_content?: string; // 兼容文本内容和文件URL
  agreement_file?: string; // 保留旧字段，向后兼容
  level_config: string[];
  approval_mode: 'any' | 'all' | 'sequence';
  use_role_based_approval: boolean;
  // === 新增：大使项目相关字段 ===
  project_type: 'survey' | 'ambassador';
  participation_limit_type: 'daily' | 'weekly' | 'monthly' | 'unlimited';
  participation_max_times: number;
  hospital_limit_enabled?: boolean;
  default_hospital_limit?: number | null;
  default_doctor_monthly_limit?: number | null;
  hospital_quotas?: Record<string, number>;
  confirm_limit_per_time?: number | null;
  review_mode?: 'manual' | 'ai';
  kv_image?: string | null;
  mobile_bg_image?: string | null;
  surveys?: Array<{
    id?: number;
    survey_id: number;
    order: number;
    is_required: boolean;
    max_times: number | null;
    is_active: boolean;
  }>;
  approvers?: Array<{
    id?: number;
    user_id: number;
    approver_type: string;
    order_index: number;
    user?: {
      id: number;
      name: string;
      phone: string;
      role: string;
    };
  }>;
  role_approvers?: Array<{
    id?: number;
    role_name: string;
    approver_type: string;
    order_index: number;
  }>;
}

export const getProjectQuotaSettings = async (id: number) => (await axiosInstance.get(`/projects/${id}/quota-settings`)).data;
export const updateProjectQuotaSettings = async (id: number, values: Partial<Project>) => (await axiosInstance.put(`/projects/${id}/quota-settings`, values)).data;

export interface Survey {
  id: number;
  title: string;
  description: string;
  type: string;
  status: string;
  price?: number;
}

export interface User {
  id: number;
  name: string;
  phone: string;
  role: string;
}

export interface Role {
  value: string;
  label: string;
  description?: string;
}

// 获取项目列表
export const getProjects = async () => {
  const response = await axiosInstance.get('/projects');
  return response.data;
};

// 获取项目详情
export const getProject = async (id: number) => {
  const response = await axiosInstance.get(`/projects/${id}`);
  return response.data;
};

// 创建项目
export const createProject = async (data: Partial<Project>) => {
  const response = await axiosInstance.post('/projects', data);
  return response.data;
};

// 更新项目
export const updateProject = async (id: number, data: Partial<Project>) => {
  const response = await axiosInstance.put(`/projects/${id}`, data);
  return response.data;
};

// 删除项目
export const deleteProject = async (id: number) => {
  const response = await axiosInstance.delete(`/projects/${id}`);
  return response.data;
};

// 导出Excel模板
export const exportTemplate = async (projectId: number) => {
  const response = await axiosInstance.get(`/projects/${projectId}/template`, {
    responseType: 'blob',
  });
  return response.data;
};

// 导出项目内所有已回答问卷（一个工作簿，每份问卷一个工作表）
// mode 为 'full' 时导出不脱敏的完整数据（含医生姓名、手机号、医院名称）
export const exportSurveyAnswers = async (projectId: number, mode: 'desensitized' | 'full' = 'desensitized') => {
  const response = await axiosInstance.get(`/projects/${projectId}/survey-answers/export`, {
    responseType: 'blob',
    params: mode === 'full' ? { mode: 'full' } : undefined,
  });
  return response.data;
};

export type SurveyExportTaskStatus = 'pending' | 'processing' | 'completed' | 'failed' | 'expired';

export interface SurveyExportTask {
  taskId: string;
  projectId: number;
  mode: 'desensitized' | 'full';
  status: SurveyExportTaskStatus;
  filename?: string;
  sheetCount?: number;
  errorMessage?: string;
  expiresAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export const createSurveyExportTask = async (
  projectId: number,
  mode: 'desensitized' | 'full' = 'desensitized'
) => {
  const response = await axiosInstance.post(`/projects/${projectId}/survey-answers/export-tasks`, { mode });
  return response.data as {
    success: boolean;
    message: string;
    data: Pick<SurveyExportTask, 'taskId' | 'status'>;
  };
};

export const getSurveyExportTask = async (taskId: string) => {
  const response = await axiosInstance.get(`/projects/survey-answer-export-tasks/${taskId}`);
  return response.data as {
    success: boolean;
    data: SurveyExportTask;
  };
};

export const downloadSurveyExportTask = async (taskId: string) => {
  const response = await axiosInstance.get(`/projects/survey-answer-export-tasks/${taskId}/download`, {
    responseType: 'blob',
    timeout: 0
  });
  return response.data as Blob;
};

export interface DoctorQuotaItem {
  doctor_id: number;
  doctor_name: string;
  doctor_phone: string;
  hospital_name: string;
  department_name: string;
  title: string;
  id_card_tail: string;
  max_submissions: number | null;
  monthly_max_submissions: number | null;
  effective_monthly_limit?: number | null;
  monthly_submitted_count: number;
  remark: string;
  submitted_count: number;
}

// 获取项目下白名单医生的提交次数配额
export const getProjectDoctorQuotas = async (
  projectId: number,
  params: { page?: number; pageSize?: number; keyword?: string } = {}
) => {
  const response = await axiosInstance.get(`/projects/${projectId}/doctor-quotas`, { params });
  return response.data;
};

// 批量设置项目下医生的提交次数配额
export const updateProjectDoctorQuotas = async (
  projectId: number,
  quotas: Array<{ doctor_id: number; max_submissions: number | null; monthly_max_submissions: number | null; remark?: string }>
) => {
  const response = await axiosInstance.put(`/projects/${projectId}/doctor-quotas`, { quotas });
  return response.data;
};

// 下载医生配额导入模板
export const downloadDoctorQuotaTemplate = async (projectId: number) => {
  const response = await axiosInstance.get(`/projects/${projectId}/doctor-quotas/template`, { responseType: 'blob' });
  return response.data as Blob;
};

export const importDoctorQuotas = async (projectId: number, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axiosInstance.post(`/projects/${projectId}/doctor-quotas/import`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 0
  });
  return response.data;
};

// 导入项目医生白名单
export const importDoctors = async (projectId: number, file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axiosInstance.post(`/projects/${projectId}/import`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// 获取问卷列表
export const getSurveys = async () => {
  const response = await axiosInstance.get('/surveys');
  return response.data;
};

// 获取问卷列表（用于项目管理的问卷选择）
export const getSurveyList = async (title?: string) => {
  const params: any = {
    page_size: 100, // 获取更多问卷
    status: 'published' // 只获取已发布的问卷
  };
  
  if (title && title.trim()) {
    params.title = title.trim();
  }
  
  const response = await axiosInstance.get('/surveys', { params });
  return {
    success: true,
    data: response.data.data
  };
};

// 获取用户列表（用于审批人选择）
export const getUsers = async () => {
  const response = await axiosInstance.get('/projects/approvers/available');
  return {
    success: true,
    data: response.data.data.map((u: any) => ({ ...u, id: u.user_id }))
  };
};

// 获取可用角色列表（用于角色审批）
export const getAvailableRoles = async () => {
  const response = await axiosInstance.get('/projects/roles/available');
  return response.data;
};

// 上传图片（如手机端问卷页背景图）
export const uploadImage = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axiosInstance.post('/upload/image', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// 上传协议文件
export const uploadAgreementFile = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axiosInstance.post('/upload/agreement', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}; 
